import { getUserContainer } from 'container/user-module'
import { LOGGED_USER_SERVICE_KEY } from 'modules/users'
import { Box, Chip, MenuItem, TextField, Typography } from '@mui/material'
import { AppTable, Field } from 'components/table'
import { useTranslation } from 'react-i18next'
import React, { useEffect, useState } from 'react'
import { Actions, Pager } from 'components/table/types'
import genericStyle from 'common/utils/generic.module.css'
import seeIcon from 'assets/articles/see.svg'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import { useIsOpenMenuLeftContext } from 'common/utils/isOpenMenuLeftContext'
import { useNavigate } from 'react-router-dom'
import styles from './AppointmentsTable.module.css'
import {
  ExternAppointment,
  ExternAppointmentQuery,
} from 'modules/calendar/models/ExternAppointment'
import {
  ExternAppointmentStatus,
  externAppointmentStatusTypes,
} from 'modules/calendar/enums/ExternAppointmentStatus'
import { dateToDateString } from 'common/utils/date'
import { getCalendarContainer } from 'container/calendar-module'
import { IExternAppointmentService } from 'modules/calendar/services/ExternAppointmentService'
import { Query, QueryParam } from 'common/api/Query'
import { EXTERN_APPOINTMENT_API_KEY } from 'modules/calendar'
import { Pagination } from 'components/table/Pagination'
import { ROUTE_APPOINTMENT_DETAIL } from 'routes/routes-constants'

const loggedUserService = getUserContainer().get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)

const calendarContainer = getCalendarContainer()
const externAppointmentService = calendarContainer.get<IExternAppointmentService>(
  EXTERN_APPOINTMENT_API_KEY
)

export function Table() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const [statusFilter, setStatusFilter] = useState<ExternAppointmentStatus | string>('every')
  const loggedUser = loggedUserService.get()

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [appointments, setAppointments] = useState<ExternAppointment[]>([])
  const [count, setCount] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const [pager, setPager] = useState<Pager>()
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const { openMenuLeft } = useIsOpenMenuLeftContext()
  const innerWidth = window.innerWidth

  useEffect(() => {
    const queryStatusFilter = [
      new QueryParam<ExternAppointmentQuery>('status', [Number(statusFilter)]),
      new QueryParam<ExternAppointmentQuery>('professionalId', loggedUser?.id ?? ''),
    ]
    externAppointmentService
      .getFilteredList(
        new Query({
          query: isNaN(Number(statusFilter))
            ? [new QueryParam<ExternAppointmentQuery>('professionalId', loggedUser?.id ?? '')]
            : queryStatusFilter,
          pager: { offset: page * rowsPerPage, limit: rowsPerPage },
        })
      )
      .subscribe((res) => {
        const appointments = res.items
        setAppointments(appointments)
        setCount(res.count)
        setIsLoading(false)
      })
  }, [isLoading, statusFilter])

  useEffect(() => {
    setIsLoading(true)
    setPager({
      page,
      count,
      handleChangePage,
      rowsPerPage,
      handleChangeRowsPerPage,
    })
  }, [page, count, rowsPerPage])

  const handleChangePage = (event: unknown, value: number) => setPage(value)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Number.isNaN(event.target.value)) {
      setRowsPerPage(10)
      return
    }
    setRowsPerPage(Number.parseInt(event.target.value))
  }

  const getChipClass = (status: ExternAppointmentStatus): string => {
    const className = `chip${ExternAppointmentStatus[status]}`
    return styles[className] || ''
  }

  const handleFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
    setStatusFilter(event.target.value)
  }

  const handleSee = (appointment: ExternAppointment) =>
    navigate(ROUTE_APPOINTMENT_DETAIL.replace(':id', appointment.id ?? ''))

  const fields: Field<ExternAppointment>[] = [
    {
      name: 'requester',
      label: t('requester'),
      renderFunc: (f, i) => i.requester.firstName + ' ' + i.requester.lastName,
    },
    {
      name: 'patient',
      label: t('resident'),
      renderFunc: (f, i) => i.patient.firstName + ' ' + i.patient.lastName,
    },
    {
      name: 'schedule',
      label: t('date'),
      renderFunc: (f, i) => dateToDateString(new Date(i.date)),
    },
    {
      name: 'status',
      label: t('status'),
      renderFunc: (f, i) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Chip
            label={externAppointmentStatusTypes()[i.status]}
            className={` ${styles.baseChip} ${getChipClass(i.status)}`}
          />{' '}
        </div>
      ),
    },
  ]

  const actions: Actions<ExternAppointment> = {
    actionsColumn: t('Actions'),
    items: [
      {
        handler: handleSee,
        icon: seeIcon,
        label: t('visibility_icon'),
      },
    ],
  }

  return (
    <div
      className={styles.parentContainer}
      style={{
        marginLeft: innerWidth > 900 ? (openMenuLeft ? '50px' : '') : '',
      }}
    >
      <Box
        style={{
          maxWidth:
            innerWidth > 900 ? (openMenuLeft ? 'calc(100% - 250px)' : 'calc(100% - 300px)') : '95%',
          position: 'relative',
        }}
        className={genericStyle.pageContainer}
      >
        <div className={genericStyle.container}>
          <div className={styles.filterRow}>
            <Typography>{t('status') + ':'}</Typography>
            <TextField
              select
              variant={'outlined'}
              className={styles.statusFilter}
              value={statusFilter}
              label={''}
              onChange={handleFilter}
              required
            >
              <MenuItem className={styles.selectItem} value={'every'}>
                {t('every')}
              </MenuItem>
              {Object.keys(ExternAppointmentStatus)
                .filter((s) => isNaN(Number(s)))
                .map((status: string) => (
                  <MenuItem
                    className={styles.selectItem}
                    value={ExternAppointmentStatus[status as keyof typeof ExternAppointmentStatus]}
                    key={status}
                  >
                    {t(status.toLowerCase())}
                  </MenuItem>
                ))}
            </TextField>
          </div>
          <AppTable
            rowKeyField="id"
            items={appointments}
            fields={fields}
            actions={actions}
            isLoading={isLoading}
            pager={pager}
          />
          {
            <Box className={styles.pagerButtons}>
              <Box sx={{ marginLeft: openMenuLeft ? '35px' : '20px' }}>
                {pager && <Pagination {...pager} />}
              </Box>
            </Box>
          }
        </div>
      </Box>
    </div>
  )
}
